import * as React from 'react';
import { RAID_REGISTER_TYPE } from '@/lib/api/registers';
import { SEVERITY_LABEL, SEVERITY_LEVELS, type SeverityLevel } from '@/components/ui/severity-chip';
import { cn } from '@/lib/utils/cn';

/**
 * RegisterFilterBar — top toolbar above the register grid.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.register` (the top row with register-type switcher + filter pills
 * + density toggle) and `prototype/ui.js → registerFilters()` which maps each
 * origin to a pill with a numeric count.
 *
 * Toolbar layout (left → right):
 *   - Register-type switcher (button group): risk_issue (RAID), finding,
 *     deliverable, deviation, interview_note — canonical values per the
 *     api/services/registers.ts truth (NOT the placeholder
 *     contract_compliance_baseline / raid / health_findings / deliverables
 *     names the scaffold shipped with; those never matched any backend row).
 *   - Origin pills (prototype's `registerFilters`):
 *       All · AI · as-is · Edited · Authored
 *     Each pill carries a count derived from the listed entries. Clicking
 *     a pill swaps the `origin` filter; "All" clears it.
 *   - Status filter (chips): all / open / mitigating / closed.
 *   - Severity filter (chips): all / critical / high / medium / low / info —
 *     the row's own severity word (C2-55), matched the way the Severity
 *     column shows it.
 *   - Density toggle: comfortable (40px row) vs compact (32px row).
 *
 * The toolbar is fully controlled — receives filter state + setters; emits no
 * side effects of its own. The route file owns the data wiring + Yjs lifecycle.
 */

export type RegisterOriginFilter =
  | 'all'
  | 'ai_proposed_or_confirmed'
  | 'consultant_edited'
  | 'manual';

export interface RegisterFiltersState {
  /** Status filter — 'all' shows every status. */
  status: 'all' | 'open' | 'mitigating' | 'closed';
  /** Severity filter — 'all' shows every severity (and rows without one). */
  severity: 'all' | SeverityLevel;
  /** Origin filter — drives the prototype's origin pill row. */
  origin: RegisterOriginFilter;
}

export const DEFAULT_REGISTER_FILTERS: RegisterFiltersState = {
  status: 'all',
  severity: 'all',
  origin: 'all',
};

export interface OriginCounts {
  total: number;
  ai: number;
  edited: number;
  manual: number;
}

export interface RegisterFilterBarProps {
  /** Currently selected register type (canonical backend value). */
  registerType: string;
  /** Setter — flips the register type. */
  onRegisterTypeChange: (next: string) => void;

  /** Known register types for this engagement. v1 hard-codes the canonical set. */
  knownRegisterTypes?: ReadonlyArray<{ value: string; label: string }>;

  /** Filter state — fully controlled by the parent. */
  filters: RegisterFiltersState;
  onFiltersChange: (next: RegisterFiltersState) => void;

  /** Density preference — compact uses 32px rows, comfortable 40px. */
  density: 'comfortable' | 'compact';
  onDensityChange: (next: 'comfortable' | 'compact') => void;

  /** Counts by origin to label the origin pills. */
  counts: OriginCounts;

  /** Hide all edit affordances (used in client-viewer scope). */
  readOnly?: boolean;
}

/**
 * Canonical register types per `apps/api/src/services/registers.ts` and the
 * health/contract register services. These are the values rows are actually
 * stored under in `register_entries.register_type` — using anything else
 * yields zero-row lists.
 *
 * Labels mirror the prototype's section vocabulary so a consultant sees the
 * same word in both surfaces (RAID for risk_issue, etc.).
 */
export const DEFAULT_REGISTER_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: RAID_REGISTER_TYPE, label: 'RAID' },
  { value: 'finding', label: 'Findings' },
  { value: 'deliverable', label: 'Deliverables' },
  { value: 'deviation', label: 'Deviations' },
  { value: 'interview_note', label: 'Interviews' },
];

export function RegisterFilterBar({
  registerType,
  onRegisterTypeChange,
  knownRegisterTypes = DEFAULT_REGISTER_TYPES,
  filters,
  onFiltersChange,
  density,
  onDensityChange,
  counts,
  readOnly = false,
}: RegisterFilterBarProps) {
  return (
    <div
      data-component="register-filter-bar"
      className="space-y-3 rounded-card border border-ink-100 bg-paper-50 p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-meta font-medium uppercase tracking-wide text-ink-500">Register</span>
        <div role="group" aria-label="Register type" className="flex flex-wrap gap-1">
          {knownRegisterTypes.map((rt) => (
            <button
              key={rt.value}
              type="button"
              onClick={() => onRegisterTypeChange(rt.value)}
              aria-pressed={rt.value === registerType}
              className={cn(
                'inline-flex h-7 items-center rounded-chip border px-2.5 text-meta font-medium transition-colors',
                rt.value === registerType
                  ? 'border-accent-amber-600 bg-accent-amber-100 text-accent-amber-700'
                  : 'border-ink-100 bg-paper-0 text-ink-700 hover:border-ink-300',
              )}
            >
              {rt.label}
            </button>
          ))}
        </div>

        <span className="ml-auto text-meta text-ink-500">
          {counts.total} {counts.total === 1 ? 'row' : 'rows'}
        </span>
      </div>

      {/* Origin pill row — mirrors prototype `registerFilters` exactly. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div role="group" aria-label="Origin filter" className="flex items-center gap-1">
          <span className="text-meta uppercase tracking-wide text-ink-500">Origin</span>
          <OriginPill
            value="all"
            label="All"
            count={counts.total}
            active={filters.origin === 'all'}
            onClick={() => onFiltersChange({ ...filters, origin: 'all' })}
          />
          <OriginPill
            value="ai_proposed_or_confirmed"
            label="AI · as-is"
            count={counts.ai}
            active={filters.origin === 'ai_proposed_or_confirmed'}
            onClick={() => onFiltersChange({ ...filters, origin: 'ai_proposed_or_confirmed' })}
          />
          <OriginPill
            value="consultant_edited"
            label="Edited"
            count={counts.edited}
            active={filters.origin === 'consultant_edited'}
            onClick={() => onFiltersChange({ ...filters, origin: 'consultant_edited' })}
          />
          <OriginPill
            value="manual"
            label="Authored"
            count={counts.manual}
            active={filters.origin === 'manual'}
            onClick={() => onFiltersChange({ ...filters, origin: 'manual' })}
          />
        </div>

        {/* Density toggle. Hidden when the route is read-only. */}
        {readOnly ? null : (
          <div role="group" aria-label="Density" className="ml-auto flex items-center gap-1">
            <span className="text-meta uppercase tracking-wide text-ink-500">Density</span>
            <DensityButton
              active={density === 'comfortable'}
              onClick={() => onDensityChange('comfortable')}
              label="Comfortable"
            />
            <DensityButton
              active={density === 'compact'}
              onClick={() => onDensityChange('compact')}
              label="Compact"
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <FilterPills
          legend="Status"
          options={[
            { value: 'all', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'mitigating', label: 'Mitigating' },
            { value: 'closed', label: 'Closed' },
          ]}
          current={filters.status}
          onChange={(next) =>
            onFiltersChange({ ...filters, status: next as RegisterFiltersState['status'] })
          }
        />

        <FilterPills
          legend="Severity"
          options={[
            { value: 'all', label: 'All' },
            ...SEVERITY_LEVELS.map((level) => ({ value: level, label: SEVERITY_LABEL[level] })),
          ]}
          current={filters.severity}
          onChange={(next) =>
            onFiltersChange({ ...filters, severity: next as RegisterFiltersState['severity'] })
          }
        />
      </div>
    </div>
  );
}

function OriginPill({
  value,
  label,
  count,
  active,
  onClick,
}: {
  value: string;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-origin-filter={value}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-chip border px-2 text-[11px] font-medium transition-colors',
        active
          ? 'border-accent-amber-600 bg-accent-amber-100 text-accent-amber-700'
          : 'border-ink-100 bg-paper-0 text-ink-700 hover:border-ink-300',
      )}
    >
      {label}
      <span
        className={cn(
          'inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 font-mono text-[10px]',
          active ? 'bg-paper-0 text-accent-amber-700' : 'bg-ink-100 text-ink-700',
        )}
      >
        {count}
      </span>
    </button>
  );
}

/** Generic pill-group used by Status + Severity filters. Single-select. */
function FilterPills<T extends string>({
  legend,
  options,
  current,
  onChange,
}: {
  legend: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  current: T;
  onChange: (next: T) => void;
}) {
  return (
    <div role="group" aria-label={legend} className="flex items-center gap-1">
      <span className="text-meta uppercase tracking-wide text-ink-500">{legend}</span>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={current === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'inline-flex h-6 items-center rounded-chip border px-2 text-[11px] font-medium transition-colors',
            current === opt.value
              ? 'border-ink-300 bg-paper-0 text-ink-900'
              : 'border-transparent text-ink-500 hover:text-ink-900',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function DensityButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-6 items-center rounded-chip border px-2 text-[11px] font-medium transition-colors',
        active
          ? 'border-ink-300 bg-paper-0 text-ink-900'
          : 'border-transparent text-ink-500 hover:text-ink-900',
      )}
    >
      {label}
    </button>
  );
}
